import { Button, HFlow, isEmpty, Text, Tooltip, VFlow } from 'bold-ui'
import { ErrorField, RadioField, usePecField } from 'components/form'
import { css } from 'emotion'
import { CiapCidSugestao, SituacaoProblema } from 'graphql/types.generated'
import { mapKeys } from 'lodash'
import React, { useMemo } from 'react'
import { Form, FormRenderProps, useForm } from 'react-final-form'
import { formatDate } from 'util/date/formatDate'
import { HOJE, humanizeDate } from 'util/date/humanize-date'
import { MetaPath } from 'util/metaPath'
import { v4 as uuidv4 } from 'uuid'
import { useEditableListField } from 'view/atendimentos/detail/components/EditableList'
import { formatCiapCid } from 'view/cuidados-compartilhados/util-cuidadoCompartilhado'

import { AvaliacaoFormModel } from '../../AvaliacaoForm'
import { ProblemaCondicaoModel } from '../problemas-condicoes/model-problemasCondicoes'
import { ciapCidCodigoToFormkey } from './operations-sugestoesProblemasHistoricoClinico'
import { sugestoesProblemasValidator } from './validator-sugestoesProblemasHistoricoClinico'

interface SugestaoProblemasIncluirLPCFormProps {
  ciapsCids: readonly CiapCidSugestao[]
  name: MetaPath<AvaliacaoFormModel>
}

export function SugestaoProblemasIncluirLPCForm(props: Readonly<SugestaoProblemasIncluirLPCFormProps>) {
  const { ciapsCids, name } = props
  const formApi = useForm<AvaliacaoFormModel>()
  const ciapsCidsMappedByFormKeys = useMemo(
    () => mapKeys(ciapsCids, (ciapCid) => ciapCidCodigoToFormkey(ciapCid.ciap?.codigo, ciapCid.cid10?.codigo)),
    [ciapsCids]
  )

  const {
    handleSubmitMultipleWithoutReset,
    input: { value: problemasECondicoesAvaliacao },
  } = useEditableListField<ProblemaCondicaoModel>({ name: name.problemasECondicoes, afterSubmit() {} })

  const {
    input: { value: hasSubmittedSugestoesDeProblemas },
  } = usePecField({ name: name.hasSubmittedSugestoesDeProblemas })

  const problemasECondicoesLPC = formApi.getState().values.problemasECondicoes
  const problemasECondicoesAtendimentoAtual = problemasECondicoesAvaliacao.concat(problemasECondicoesLPC)

  const onSubmit = (values: SituacaoProblema[]) => {
    const formKeys: string[] = Object.keys(values)
    const problemasToSubmit = []

    formKeys.forEach((key) => {
      const formValue = ciapsCidsMappedByFormKeys[key]
      const isCiapCidJaCadastrada = !problemasECondicoesAtendimentoAtual.some(
        (problemaCondicao: ProblemaCondicaoModel) =>
          problemaCondicao?.ciap?.codigo === formValue.ciap?.codigo &&
          problemaCondicao?.cid?.codigo === formValue.cid10?.codigo &&
          problemaCondicao.incluirNaListaProblemas
      )

      if (isCiapCidJaCadastrada) {
        const problemaCondicao: ProblemaCondicaoModel = {
          _id: uuidv4(),
          ciap: formValue?.ciap,
          cid: formValue?.cid10,
          situacaoProblema: values[key],
          isAutomatico: false,
          incluirNaListaProblemas: true,
        }
        problemasToSubmit.push(problemaCondicao)
      }
    })

    if (!isEmpty(problemasToSubmit)) {
      handleSubmitMultipleWithoutReset(problemasToSubmit, formApi)
    }
    formApi.change(name.hasSubmittedSugestoesDeProblemas.absolutePath(), true)
  }

  const render = ({ handleSubmit }: FormRenderProps) => {
    var countCiapsCidsCadastradas = 0
    return (
      <VFlow>
        {ciapsCids?.map((ciapCid) => {
          const isCiapCidJaCadastrada = problemasECondicoesAtendimentoAtual.some(
            (problemaCondicao: ProblemaCondicaoModel) =>
              problemaCondicao?.ciap?.codigo === ciapCid.ciap?.codigo &&
              problemaCondicao?.cid?.codigo === ciapCid.cid10?.codigo &&
              problemaCondicao.incluirNaListaProblemas
          )
          if (isCiapCidJaCadastrada) {
            countCiapsCidsCadastradas = countCiapsCidsCadastradas + 1
          }
          const formKey = ciapCidCodigoToFormkey(ciapCid?.ciap?.codigo, ciapCid?.cid10?.codigo)
          const humanizedDate = humanizeDate(ciapCid.dataRegistroProblema)
          return (
            <Tooltip
              text={isCiapCidJaCadastrada ? 'Registro já adicionado na lista de problemas/condições' : null}
              placement='auto-start'
              key={formKey}
            >
              <VFlow vSpacing={0.2} key={formKey}>
                <Text>
                  {formatCiapCid(ciapCid.ciap, ciapCid.cid10).toUpperCase()}
                  <Text color='danger'>*</Text>
                </Text>
                <Text color='secondary'>
                  Registro em {formatDate(ciapCid.dataRegistroProblema)} - {humanizedDate !== HOJE && 'Há '}
                  {humanizedDate}
                </Text>
                <HFlow
                  style={css`
                    margin-left: -0.3rem;
                  `}
                >
                  <RadioField
                    name={formKey}
                    label='Ativo'
                    value={SituacaoProblema.ATIVO}
                    disabled={hasSubmittedSugestoesDeProblemas || isCiapCidJaCadastrada}
                  />
                  <RadioField
                    name={formKey}
                    label='Resolvido'
                    value={SituacaoProblema.RESOLVIDO}
                    disabled={hasSubmittedSugestoesDeProblemas || isCiapCidJaCadastrada}
                  />
                </HFlow>
                <ErrorField name={formKey} />
              </VFlow>
            </Tooltip>
          )
        })}
        <Button
          size='small'
          skin='default'
          kind='primary'
          onClick={handleSubmit}
          disabled={hasSubmittedSugestoesDeProblemas || ciapsCids.length <= countCiapsCidsCadastradas}
        >
          Adicionar
        </Button>
      </VFlow>
    )
  }

  return (
    <Form
      onSubmit={onSubmit}
      render={render}
      validate={sugestoesProblemasValidator(props.ciapsCids, problemasECondicoesAtendimentoAtual)}
    />
  )
}
